import { memo, useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Button, LinkButton } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Form,
  FormGroup,
  RadioGroup,
  Select,
  Text
} from 'components/common/form';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { useEditorDispatch } from 'components/editor';
import { editorSocket } from 'components/editor/socket';

import type { FormApi } from 'informed';
import type { EditorHeaderSelectedBlocksProps } from './EditorHeaderSelectedBlocks';

type Props = Pick<
  EditorHeaderSelectedBlocksProps,
  | 'chapters'
  | 'currentChapter'
  | 'isCopyingBlocks'
  | 'isCopyingBlocksSuccess'
  | 'selectedBlockIds'
>;

const EditorHeaderSelectedBlocksCopy = ({
  chapters,
  currentChapter,
  isCopyingBlocks,
  isCopyingBlocksSuccess,
  selectedBlockIds
}: Props) => {
  const { dispatch } = useDispatch();

  const editorDispatch = useEditorDispatch();

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const [modalVisible, setModalVisible] = useState(false);
  const [targetChapterId, setTargetChapterId] = useState<number>(null);

  const handleSubmit = async (values) => {
    if (values.chooseTransferAction === 'new') {
      try {
        const newChapter = await api.post(
          `/admin/courses/${course.id}/chapters`,
          { title: values.newChapterTitle }
        );

        values.chapterId = newChapter.data.chapter.id;

        const chapters = await api.get(`/admin/courses/${course.id}/chapters`);

        editorDispatch({
          type: 'updateContext',
          update: {
            editorChapters: chapters.data.chapters
          }
        });
      } catch (err) {
        await dispatch.handleErrorDispatch(err);

        hideModal();
        return false;
      }
    }

    setTargetChapterId(values.chapterId);

    editorSocket.copyBlocks({
      chapterId: currentChapter.id,
      blockIds: selectedBlockIds,
      targetChapterId: values.chapterId
    });
  };

  const hideModal = () => {
    formApi.current?.reset();
    editorDispatch({
      type: 'updateContext',
      update: { editorCopyBlocksLoading: false, editorCopyBlocksSuccess: false }
    });
    setModalVisible(false);
  };

  const showModal = () => setModalVisible(true);

  const targetChapter = targetChapterId
    ? chapters.find((c) => c.id === targetChapterId)
    : null;

  return (
    <>
      <Button
        aria-label={messages.editorHeaderCopyBlocks}
        data-tooltip={messages.editorHeaderCopyBlocks}
        category="light"
        icon={['far', 'copy']}
        onClick={showModal}
        size="small"
        style={{ marginRight: 10 }}
      />

      <ModalPortal>
        <Form
          getApi={(form) => (formApi.current = form)}
          onSubmit={handleSubmit}
        >
          {({ formState }) => (
            <Modal
              onHide={hideModal}
              title={messages.editorHeaderCopyBlocks}
              visible={modalVisible}
            >
              <ModalContent>
                {!isCopyingBlocksSuccess ? (
                  <div>
                    <FormGroup label={messages.editorHeaderCopyBlocksChoose}>
                      <RadioGroup
                        field="chooseTransferAction"
                        initialValue="exist"
                        options={[
                          {
                            id: 'chooseTransferAction-copy-exist',
                            label:
                              messages.editorHeaderTransferBlocksWhereExist,
                            value: 'exist'
                          },
                          {
                            id: 'chooseTransferAction-copy-new',
                            label: messages.editorHeaderTransferBlocksWhereNew,
                            value: 'new'
                          }
                        ]}
                      />
                    </FormGroup>

                    {formState.values.chooseTransferAction === 'exist' ? (
                      <FormGroup label={messages.editorHeaderCopyBlocksChapter}>
                        <Select
                          field="chapterId"
                          id="selected-blocks-copy-chapter-id"
                          initialValue={chapters[0].id}
                          isSearchable={true}
                          options={chapters.map((chapter) => ({
                            label: chapter.title,
                            value: chapter.id
                          }))}
                        />
                      </FormGroup>
                    ) : (
                      <FormGroup label={messages.editChapterTitle}>
                        <Text field="newChapterTitle" id="new-chapter-title" />
                      </FormGroup>
                    )}
                  </div>
                ) : (
                  <div className="success">
                    <div className="success-icon">
                      <Icon icon="check" />
                    </div>

                    <div className="success-text">
                      {messages.editorHeaderCopyBlocksSuccess}
                    </div>

                    <div className="success-actions">
                      {targetChapter?.id !== currentChapter.id ? (
                        <LinkButton
                          category="default"
                          href={`/admin/courses/${course.slug}/edit/${targetChapter?.slug}`}
                          style={{ width: '100%' }}
                          text={`${messages.editorHeaderGoToChapter} ${targetChapter?.title}`}
                        />
                      ) : null}

                      <Button
                        category="light"
                        onClick={hideModal}
                        style={{ marginTop: 15, width: '100%' }}
                        text={messages.editorHeaderContinue}
                      />
                    </div>

                    <style jsx>{`
                      .success {
                        text-align: center;
                      }
                      .success-icon {
                        background-color: ${theme.successColor};
                        border-radius: 50%;
                        color: ${theme.successTextColor};
                        display: inline-block;
                        font-size: 1rem;
                        height: 26px;
                        margin-top: 10px;
                        padding: 7px 0;
                        text-align: center;
                        width: 26px;
                      }
                      .success-text {
                        margin-top: 10px;
                      }
                      .success-actions {
                        margin-top: 20px;
                      }
                      .success-or {
                        margin: 5px 0;
                      }
                    `}</style>
                  </div>
                )}
              </ModalContent>

              {!isCopyingBlocksSuccess ? (
                <ModalFooter>
                  <Button
                    category="light"
                    onClick={hideModal}
                    text={messages.commonCancel}
                  />

                  <Button
                    category="success"
                    disabled={
                      formState.values.chooseTransferAction === 'new' &&
                      !formState.values.newChapterTitle
                    }
                    loading={isCopyingBlocks}
                    text={messages.commonCopy}
                    type="submit"
                  />
                </ModalFooter>
              ) : null}
            </Modal>
          )}
        </Form>
      </ModalPortal>
    </>
  );
};

export default memo(EditorHeaderSelectedBlocksCopy);
